import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X, ArrowRight } from 'lucide-react';
import brands from '../data/brands.json';
import company from '../data/company.json';
import styles from './Navbar.module.css';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const dropRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setBrandsOpen(false);
  }, [location]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setBrandsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll to section handler
  const scrollToSection = (sectionId) => {
    setMobileOpen(false);
    setBrandsOpen(false);

    if (location.pathname !== '/') {
      // Navigate to home first, then scroll
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isHome = location.pathname === '/';

  return (
    <header className={`${styles.header} ${scrolled || !isHome ? styles.scrolled : ''}`}>
      <div className={styles.inner}>
        {/* Logo */}
        <Link to="/" className={styles.logo}>
          <img src={company.logo} alt={company.name} className={styles.logoImg} />
          <span className={styles.logoText}>
            AL <span className={styles.logoAccent}>Motamakina</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className={styles.nav}>
          <Link to="/" className={styles.navLink}>Home</Link>
          <button className={styles.navLink} onClick={() => scrollToSection('about')}>About</button>

          {/* Products Dropdown */}
          <div className={styles.dropWrap} ref={dropRef}>
            <button
              className={`${styles.navLink} ${styles.dropTrigger}`}
              onClick={() => setBrandsOpen((v) => !v)}
              aria-expanded={brandsOpen}
            >
              Products <ChevronDown size={15} className={brandsOpen ? styles.chevronOpen : ''} />
            </button>
            {brandsOpen && (
              <div className={styles.dropdown}>
                <p className={styles.dropLabel}>Brands We Represent</p>
                <div className={styles.dropGrid}>
                  {brands.map((brand) => (
                    <Link key={brand.id} to={`/brands/${brand.slug}`} className={styles.dropItem}>
                      <span className={styles.dropDot} style={{ background: brand.color }} />
                      <div>
                        <span className={styles.dropName}>{brand.name}</span>
                        <span className={styles.dropSub}>{brand.tagline}</span>
                      </div>
                      <ArrowRight size={14} className={styles.dropArrow} />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button className={styles.navLink} onClick={() => scrollToSection('services')}>Services</button>
          <button className={`${styles.navLink} ${styles.navCta}`} onClick={() => scrollToSection('contact')}>Contact Us</button>
        </nav>

        {/* Mobile Toggle */}
        <button className={styles.hamburger} onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle menu">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className={styles.mobileMenu}>
          <Link to="/" className={styles.mobileLink}>Home</Link>
          <button className={styles.mobileLink} onClick={() => scrollToSection('about')}>About</button>
          <div className={styles.mobileBrandsLabel}>Products by Brand</div>
          {brands.map((b) => (
            <Link key={b.id} to={`/brands/${b.slug}`} className={styles.mobileLink} style={{ paddingLeft: 32 }}>
              <span className={styles.dropDot} style={{ background: b.color }} />
              {b.name}
            </Link>
          ))}
          <button className={styles.mobileLink} onClick={() => scrollToSection('services')}>Services</button>
          <button className={styles.mobileLink} onClick={() => scrollToSection('contact')}>Contact Us</button>
        </div>
      )}
    </header>
  );
}